import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CodeBlock } from '@/components/docs/CodeBlock';

export default function IntroductionPage() {
  return (
    <article className="max-w-3xl" data-testid="docs-introduction">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Introduction</h1>
      <p className="text-base text-white/50 leading-relaxed mb-8">
        GhostLayout is an open-source library that automatically generates pixel-perfect skeleton loading screens by analyzing your actual UI components.
      </p>

      <div className="border border-white/[0.06] rounded-lg bg-[#0A0A0A] p-6 mb-8">
        <h3 className="font-bold text-sm mb-3">The problem</h3>
        <p className="text-sm text-white/40 leading-relaxed mb-4">
          Manually building skeleton loaders is tedious, error-prone, and falls out of sync every time you change your component layout. You end up maintaining two versions of every UI — the real one and the placeholder.
        </p>
        <h3 className="font-bold text-sm mb-3">The solution</h3>
        <p className="text-sm text-white/40 leading-relaxed">
          GhostLayout uses a headless browser to visit your running app, measure every element inside your components, and generate a JSON blueprint of the layout. At runtime, the <code className="text-white/60 bg-white/[0.06] px-1.5 py-0.5 rounded text-xs">&lt;Skeleton&gt;</code> component renders those blueprints as animated placeholders.
        </p>
      </div>

      <h2 className="text-xl font-bold tracking-tight mb-4 mt-12">How it works</h2>

      <div className="space-y-4 mb-8">
        <div className="flex gap-4">
          <div className="w-8 h-8 rounded-md bg-white/[0.06] flex items-center justify-center shrink-0 text-xs font-mono font-bold text-white/40">1</div>
          <div>
            <h4 className="font-semibold text-sm mb-1">Wrap your components</h4>
            <p className="text-sm text-white/40">Add <code className="text-white/60 bg-white/[0.06] px-1 py-0.5 rounded text-xs">&lt;Skeleton&gt;</code> around any component that needs a loading state.</p>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="w-8 h-8 rounded-md bg-white/[0.06] flex items-center justify-center shrink-0 text-xs font-mono font-bold text-white/40">2</div>
          <div>
            <h4 className="font-semibold text-sm mb-1">Run the CLI</h4>
            <p className="text-sm text-white/40">The CLI opens your app in a headless browser and extracts the layout of every wrapped component at multiple breakpoints.</p>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="w-8 h-8 rounded-md bg-white/[0.06] flex items-center justify-center shrink-0 text-xs font-mono font-bold text-white/40">3</div>
          <div>
            <h4 className="font-semibold text-sm mb-1">Import the registry</h4>
            <p className="text-sm text-white/40">One import connects all generated skeletons. Every <code className="text-white/60 bg-white/[0.06] px-1 py-0.5 rounded text-xs">&lt;Skeleton&gt;</code> auto-resolves by name.</p>
          </div>
        </div>
      </div>

      <h2 className="text-xl font-bold tracking-tight mb-4 mt-12">Quick example</h2>
      <CodeBlock
        filename="app/page.tsx"
        code={`import { Skeleton } from '@ghostlayout/react'
import './blueprints/registry'

function BlogPage() {
  const { data, isLoading } = useFetch('/api/post')

  return (
    <div data-skeleton-name="blog-card">
      <Skeleton name="blog-card" loading={isLoading}>
        {data && <BlogCard data={data} />}
      </Skeleton>
    </div>
  )
}`}
      />

      <div className="mt-10 flex gap-3">
        <Link to="/docs/installation" className="inline-flex items-center gap-2 bg-white text-black px-4 py-2 rounded-md text-sm font-semibold hover:bg-gray-200 transition-colors" data-testid="next-installation-btn">
          Installation <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </article>
  );
}
